/* eslint-disable max-lines, max-depth, max-statements, complexity */
/********************************/
/*          About vuex          */
/********************************/
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.
import { mapActions, mapState } from "vuex";
import config from "../../../../../share/config";
import session from "../../../../../share/session";
import individualDiagnose from "../../../../../share/util/individualDiagnose";
import {
  DATA_TYPE,
  ERR_KEY,
  ERR_TYPE,
  FLOW,
  MNTR_STATUS,
  NRC_CODE,
  RES_TYPE,
  HEX_PREFIX,
  FLOW_ERROR,
  REQUEST_RESULT,
  AT_EXE_TYPE
} from "../../../../pages/individualActiveTest/individualActiveTestConstants.js";
import atUtil from "../../../../pages/individualActiveTest/individualActiveTestUtil";
import loading from "../../../../../share/components/vue/ProcessingIcon";
import sp1Modal from "../../../../../components/vue/individualActiveTest/activeTestControlPanel/RequirementSP1Modal";

const MENUS = {
  ATID: "active_test_id",
  REQID: "requirement_id",
  TITLEID: "title_id",
  VALS: "values",
  SID22: "sid0x22",
  SID2E2F: "sid0x2E_sid0x2F",
  CTRL_STT: "control_state",
  DID: "did",
  VAL: "value",
  RID: "rid",
  EXE_MNTR_TERMINATE: "execution_monitoring_terminate_condition",
  EXE_MNTR_CONTINUE: "execution_monitoring_continuate_condition",
  STOP_MNTR_TERMINATE: "stop_monitoring_terminate_condition",
  STOP_MNTR_CONTINUE: "stop_monitoring_continuate_condition",
};

const CONV = {
  DID: "did",
  INFO: "info",
  DTYPE: "data_type",
};

const AT_EXE = {
  STATUS: "status",
  MNTR_STATUS: "monitoring_status",
  USER_DATA: "user_data",
  RES_TYPE: "res_type",
  VAL: "value",
  REQUEST_RESULT: "request_result",
};

const I18N = {
  MENU_TITLE_REF: "menu_title_ref",
  ITEM_NAME: "item_name",
};

const REQ_ID = {
  SP1: "REQ.ACT.SP1",
  SP10: "REQ.ACT.SP10",
  SP15: "REQ.ACT.SP15",
  SP20: "REQ.ACT.SP20",
  SP22: "REQ.ACT.SP22",
  SP24: "REQ.ACT.SP24",
  SP27: "REQ.ACT.SP27",
};

// "REQ.ACT.SP10"の以下についてはボタンにHEX値をそのまま表示する
//   100301 パワーバランス(噴射停止) - ポート噴射
//   100401 パワーバランス(噴射停止) - 筒内噴射
//   100501 パワーバランス(噴射停止) - 制御依存噴射
//   100601 パワーバランス(噴射点火停止) - ポート噴射
//   100701 パワーバランス(噴射点火停止) - 筒内噴射
//   100801 パワーバランス(噴射点火停止) - 制御依存噴射
const SPECIAL_BUTTON = {
  "REQ.ACT.SP10": [
    "100301",
    "100401",
    "100501",
    "100601",
    "100701",
    "100801"
  ]
};

const BTN_STYLE_INACTIVE = {
  ["long-btn"]: true,
  ["inactive-btn"]: true
};

const BTN_STYLE_ACTIVE = {
  ["long-btn"]: true,
  ["inactive-btn"]: false
};

const NO_ERROR_STOP_REQ = [
  "REQ.ACT.SP1",
];

// SP1Modalフェイズ
const SP1_PHASE = {
  CALIBRATION_CONFIRM: 'calibrationConfirm',
  CALIBRATION_IN_PROGRESS: 'calibrationInProgress',
  CALIBRATION_FAILURE: 'calibrationFailure',
  EXECUTE_CONFIRM_NO_CALIBRATION: 'executeConfirmNoCalibration',
  EXECUTE_CONFIRM_WITH_CALIBRATION: 'executeConfirmWithCalibration',
};

export default {
  name: "activeTestButtonPanel",
  data() {
    return {
      /** 状態遷移 */
      flow: FLOW.INIT,
      /** 変換情報 */
      conversionInfoList: [], //(doStart, doStop, エラーの停止処理以外で変更してはならない)
      /** 停止中フラグ */
      isStopping: false,
      /** ボタンデータ */
      btns: [],
      /** 選択中のボタン */
      selectedBtn: null,
      /** 実行/実行監視中に押下されたボタン(SP24シフト表示B表示) */
      changedBtn: null,
      /** AT文言 */
      message: "",
      displayProcessing: false,
      // サインアウト
      signout: false,
      // SP1Modal表示中フラグ
      isSp1ModalFlow: false,
    };
  },

  props: {
    // 選択されたAT項目
    selectedAtFunc: { type: Object, required: true },
    // 選択項目名称i18nキー
    i18nMenuTitleKey: { type: String, default: "", required: false },
    // 駆動中フラグ、親画面の画面遷移判定で使用
    isDriving: { type: Boolean, required: true },
    // DM駆動中フラグ
    inExecution: { type: Boolean, required: true },
    // 翻訳ファイルのrefから構築したボタン用のデータ SP24のみ
    btnRefDataList: { type: Array, default: () => ([]), required: false },
    // DM項目選択画面表示フラグ
    showDmSelect: { type: Boolean, required: false },
    // DMエラー発生フラグ
    isDmError: { type: Boolean, required: false },
  },

  components: {
    "processing-view": loading,
    "sp1-modal": sp1Modal,
  },

  watch: {
    // 言語設定変更時処理
    language: {
      async handler() {
        this.clearAtMsg();
        this.btns = await this.createBtnData();
        if (this.flow === FLOW.STANDBY) {
          this.selectedBtn = null;
        } else {
          this.disableAllItemBtns();
        }
      },
    },
  },

  computed: {
    ...mapState({
      language: (state) => state.systems.language,
      netAppId: (state) => state.netAppConnector.pairingAppId,
      individualMenus: state => state.vehicle.individualMenus,
    }),
    systemId() {
      return individualDiagnose.getCurrentSystemId(this);
    },
    activeTestId() {
      return this.selectedAtFunc[MENUS.ATID];
    },
    requirementId() {
      return this.selectedAtFunc[MENUS.REQID];
    },
    ridList() {
      return this.selectedAtFunc[MENUS.RID];
    },
    controlStateList() {
      return this.selectedAtFunc[MENUS.CTRL_STT];
    },
    uiid() {
      return this.selectedAtFunc[MENUS.ATID].slice(0, -2);
    },
    isDriveFlow() {
      return [FLOW.PRE_EXECUTE, FLOW.EXECUTE, FLOW.CHECK_EXE].includes(this.flow);
    },

    driveBtnLabel() {
      // 駆動中、もしくはSP15の場合は常に[停止]
      return this.isDriving || this.requirementId === REQ_ID.SP15
        ? this.$t("individual.button_data_monitor_stop")
        : this.$t("individual.button_active_test_drive");
    },

    btnDisabled() {
      const disabled = {
        drive: this.getDriveBtnDisabled(),
      };
      return disabled;
    },
  },

  methods: {
    ...mapActions("auth", ["logout", "resetSession"]),

    /** SP1モーダルクローズ */
    closeWizard() {
      this.isSp1ModalFlow = false;
    },

    /**
     * AT項目選択ボタンクリック時の処理
     * AT項目選択 → DM項目選択 遷移時に行う処理を記述する
     */
    async btnAtSelectOnClicked() {
      this.displayProcessing = true;
      // データ読み出し実行フラグをfalseに切り替え
      this.$emit("toggle-read-executed", false);
      // ボタンデータの作成
      this.btns = await this.createBtnData();
      // SP1Modal表示
      if (this.requirementId === REQ_ID.SP1) {
        this.isSp1ModalFlow = true;
      }
      // 初期表示処理
      this.displayInit();
      this.displayProcessing = false;
    },

    /**
     * 初期表示処理
     * flowをSTANDBYへ遷移させ、駆動ボタンの表示を「駆動」に切り替える
     */
    displayInit() {
      // SP24 シフト表示-B表示 : 停止後にほかの機能に遷移する以外の時
      if (!this.changedBtn) {
        // 待機に遷移
        this.flow = FLOW.STANDBY;
        // 駆動状態を解除 (DMエラー発生時はエラーハンドリングのため駆動状態を解除しない)
        if (!this.isDmError) {
          this.$emit("toggle-drive", false);
        }
        // 全項目ボタンの活性化
        this.enableAllItemBtns();
        // 選択ボタンの解除
        this.selectedBtn = null;
      }

      if ((this.btns) && (Array.isArray(this.btns))) {
        this.btns.forEach((btn) => {
          // SP24 シフト表示 B表示 : 停止後にほかの機能に遷移する場合、遷移後のボタンを選択中表示にする
          if (this.changedBtn && btn.key === this.changedBtn.key) {
            btn.style = BTN_STYLE_ACTIVE;
          } else {
            btn.style = BTN_STYLE_INACTIVE;
          }
        });
      }
    },

    /** AT文言の削除 */
    clearAtMsg() {
      this.message = "";
    },

    changeFlow(newFlow) {
      if (Object.values(FLOW).includes(newFlow)) {
        this.flow = newFlow;
      }
    },

    /** 項目ボタン押下時の処理 */
    async btnClick(clickedBtn) {
      // SP24 シフト表示 B表示 : 実行/実行監視中に項目ボタンが押下された場合
      if (this.selectedBtn && this.selectedBtn.key !== clickedBtn.key
        && (this.flow === FLOW.PRE_EXECUTE || this.flow === FLOW.EXECUTE || this.flow === FLOW.CHECK_EXE)
      ) {
        this.btns.forEach((btn) => {
          if (btn.key === clickedBtn.key) {
            btn.style = BTN_STYLE_ACTIVE;
          } else {
            btn.style = BTN_STYLE_INACTIVE;
          }
        });
        this.changedBtn = clickedBtn;
        // 変化前のselectedBtnで停止ボタン押下処理実行
        await this.stopDrive();
        this.selectedBtn = clickedBtn;
        // 変化後のselectedBtnで駆動ボタン押下処理実行
        await this.startDrive();
        this.changedBtn = null;
      } else {
        this.btns.forEach((btn) => {
          if (btn.key === clickedBtn.key) {
            this.selectedBtn = clickedBtn;
            btn.style = BTN_STYLE_ACTIVE;
          } else {
            btn.style = BTN_STYLE_INACTIVE;
          }
        });

        if (
          this.requirementId === REQ_ID.SP1 ||
          this.requirementId === REQ_ID.SP15
        ) {
          // SP1,15の場合は即時駆動
          await this.startDrive();
        }
      }
      return clickedBtn;
    },

    /** 全項目ボタンの非活性化 */
    disableAllItemBtns() {
      this.btns.forEach((btn) => {
        // SP24 シフト表示 B表示 : 実行/実行監視中のみ全項目ボタンの非活性化しない
        if ((this.requirementId === REQ_ID.SP24 && this.activeTestId === "246301" && this.selectedBtn.key === 13)
          && (this.flow === FLOW.PRE_EXECUTE || this.flow === FLOW.EXECUTE || this.flow === FLOW.CHECK_EXE)) {
          return;
        }
        btn.disabled = true;
      });
    },

    /** 全項目ボタンの活性化 */
    enableAllItemBtns() {
      this.btns.forEach((btn) => {
        btn.disabled = false;
      });
    },

    /** 駆動ボタンの活性・非活性 */
    getDriveBtnDisabled() {
      const res = ![
        FLOW.STANDBY,
        FLOW.PRE_EXECUTE,
        FLOW.EXECUTE,
        FLOW.CHECK_EXE,
      ].includes(this.flow) ||
        !this.selectedBtn;
      return res;
    },

    /** 駆動ボタン押下時イベント */
    clickDriveBtn() {
      if (this.isDriving) {
        // 停止
        this.stopDrive();
      } else if (this.requirementId !== REQ_ID.SP15) {
        // SP15以外の場合は駆動
        this.startDrive();
      }
    },

    /** 駆動ボタン押下処理 */
    async startDrive() {
      this.$emit("toggle-drive", true);
      this.flow = FLOW.PRE_EXECUTE;
      this.disableAllItemBtns();
      this.clearAtMsg();

      // 開始マーカの登録実行
      const markInfo = {
        "type": AT_EXE_TYPE.START, // 駆動をセット
        "info": [
          {
            "value": this.selectedBtn.name
          }
        ]
      };
      await this.$emit("save-active-test-executeMark", markInfo);

      const response = await atUtil.doStandby(this, true);
      if (response) {
        switch (response.data.status) {
          case config.STATUS_DMAT_COMPLETED:
            {
              let checkResponse = true;
              if (response.data.user_data && Array.isArray(response.data.user_data)) {
                for (const userData of response.data.user_data) {
                  if (userData.res_type !== RES_TYPE.OK) {
                    checkResponse = false;
                    break;
                  }
                }
              }
              // 正常終了
              if (checkResponse) {
                // AT実行
                await this.doStart();
              }
              else {
                // 共通エラーへ遷移
                this.flow = FLOW.ERR;
                this.doErrorProcess(ERR_KEY.COMMON);
              }
            }
            break;
          default:
            // 共通エラーへ遷移
            this.flow = FLOW.ERR;
            this.doErrorProcess(ERR_KEY.COMMON);
            break;
        }
      }
    },

    /** 停止ボタン押下処理 */
    async stopDrive() {
      this.flow = FLOW.STOP;
      this.disableAllItemBtns();

      // 停止マーカの登録実行
      const markInfo = {
        "type": AT_EXE_TYPE.END, // 停止をセット
      };
      await this.$emit("save-active-test-executeMark", markInfo);

      const response = await this.doStop();
      if (response) {
        if (response.data.status === config.STATUS_DMAT_COMPLETED) {
          const result = await this.doTerminate();
          if (result) {
            // 初期表示処理
            this.displayInit();

            this.$emit("toggle-drive", false);

            // アクティブ文言のクリア判定
            if (atUtil.isClearMessageOnStop(this.requirementId)) {
              this.clearAtMsg();
            }
          } else {
            // 共通エラーへ遷移
            this.flow = FLOW.ERR;
            this.doErrorProcess(ERR_KEY.COMMON);
          }
        }
      } else {
        // 共通エラーへ遷移
        this.flow = FLOW.ERR;
        this.doErrorProcess(ERR_KEY.COMMON);
      }
    },

    /** ボタンに登録するデータのオブジェクトの作成 */
    async createBtnData() {
      let res = [];
      switch (this.requirementId) {
        case REQ_ID.SP1:
          res = this.createBtnDataSP1(this.controlStateList);
          break;
        case REQ_ID.SP10:
          res = await this.createBtnDataUserInput(
            this.controlStateList,
            this.conversionInfoList
          );
          break;
        case REQ_ID.SP15:
          res = this.createBtnDataSP15(this.controlStateList);
          break;
        case REQ_ID.SP20:
        case REQ_ID.SP22:
        case REQ_ID.SP27:
          this.ridList.forEach((rid, idx) => {
            res.push({
              name: this.getRIDI18nName(rid),
              key: idx,
              value: null,
              sid: rid,
              disabled: false,
              style: BTN_STYLE_INACTIVE,
            });
          });
          break;
        case REQ_ID.SP24:
          res = this.createBtnDataRefs();
          break;
        default:
          break;
      }
      return res;
    },

    /** RIDに対応する翻訳を取得 */
    getRIDI18nName(rid) {
      const key = `at${this.systemId}.${this.uiid}.item_name_${rid}`;
      return this.$te(key) ? this.$t(key) : "";
    },

    /** ユーザ入力値制限のボタンデータ作成 */
    async createBtnDataUserInput(controlStateList) {
      const res = [];

      // 変換情報の取得
      if (!(await this.getATConversion())) return res;

      // ボタンでは変換情報は単一
      let conversionInfo = null;
      for (const convInfo of this.conversionInfoList) {
        if (convInfo[CONV.DID] === this.selectedAtFunc[MENUS.SID2E2F][0]) {
          conversionInfo = convInfo;
        }
      }

      const didList = [];
      const tmpList = [];
      controlStateList.forEach((controlState, idx) => {
        if (controlState.value) {
          let labelName;

          // HEX値のまま表示する特別ケースに該当する場合は0xをつけて表示する
          const specialButtons = SPECIAL_BUTTON[this.requirementId]?.includes(this.activeTestId);
          if (specialButtons) {
            labelName = `${HEX_PREFIX}${controlState.value}`;

          } else {
            const hexKey = atUtil.convCtrlStateValue(
              controlState.value,
              conversionInfo
            );
            labelName = hexKey;

            // valueをDID変換し、HexとFlgの場合は翻訳して表示する
            const systemId = individualDiagnose.getCurrentSystemId(this);
            if (conversionInfo[CONV.INFO][CONV.DTYPE] !== DATA_TYPE.VAL) {
              const did = conversionInfo[CONV.DID];

              // diag/配下の検索キー
              const findFFDi18nKey = individualDiagnose.getFFDi18nKey(this, this.individualMenus ? this.individualMenus.menu_list : []);
              const keyDiag = `${findFFDi18nKey}.${did}_${hexKey}`;
              // at/did/配下の検索キー
              const keyAt = `atDid${systemId}.${did}_${hexKey}`;
              if (this.$te(keyDiag)) {
                labelName = this.$t(keyDiag);
              } else if (this.$te(keyAt)) {
                labelName = this.$t(keyAt);
              } else {
                // データタイプがHEXで、翻訳が取得できなかった場合は、HEX値を直接表示
                if (conversionInfo[CONV.INFO][CONV.DTYPE] === DATA_TYPE.HEX) {
                  labelName = `${HEX_PREFIX}${hexKey}`;
                }
              }
            }
          }

          res.push({
            name: labelName,
            key: idx,
            value: controlState.value,
            sid: this.selectedAtFunc[MENUS.SID2E2F][0],
            disabled: false,
            style: BTN_STYLE_INACTIVE,
          });
        } else if (controlState.did) {
          // 一旦resにnullを格納し、一時リストを作成する
          res.push(null);
          didList.push(controlState.did);
          tmpList.push({
            name: null,
            key: idx,
            value: controlState.did,
            sid: this.selectedAtFunc[MENUS.SID2E2F][0],
            disabled: false,
            style: BTN_STYLE_INACTIVE,
          });
        }
      });

      if (didList.length) {
        // データ読み出し
        const response = await atUtil.doRead(this, didList);

        // レスポンスがとれてないなら異常なので[]を返す
        if (
          !response ||
          !response.data ||
          !response.data[AT_EXE.USER_DATA] ||
          response.data[AT_EXE.USER_DATA].length !== tmpList.length
        ) {
          return [];
        }

        // データ読み出し実行フラグをtrueに切り替え
        this.$emit("toggle-read-executed", true);

        // DID変換
        response.data[AT_EXE.USER_DATA].forEach((userData, idx) => {
          const val = atUtil.convCtrlStateValue(userData, conversionInfo);
          tmpList[idx].name = val;
        });

        // resのnull部分にtmpListを順番に設定
        let count = 0;
        res.forEach((val, idx, arr) => {
          if (val === null) {
            arr[idx].name = tmpList[count];
            count++;
          }
        });
      }

      return res;
    },

    /** 親子構造を持つ翻訳ファイルからボタンデータを作成 */
    createBtnDataRefs() {
      const res = [];

      if (!this.selectedAtFunc[MENUS.VALS]) return res;

      let count = 0;
      this.selectedAtFunc[MENUS.VALS][0].forEach((menus) => {
        const uuid = menus[MENUS.ATID].slice(0, -2);
        const menuTitleKey = `at${this.systemId}.${uuid}.${I18N.MENU_TITLE_REF}`;
        const menuTitleRef = this.$te(menuTitleKey)
          ? this.$t(menuTitleKey)
          : "";

        // menu_title_refが一致するなら表示するボタンに追加
        if (menuTitleRef === this.selectedAtFunc[MENUS.TITLEID]) {
          const itemNameKey = `at${this.systemId}.${uuid}.${I18N.ITEM_NAME}`;
          const itemName = this.$te(itemNameKey) ? this.$t(itemNameKey) : "";
          res.push({
            name: itemName,
            key: count,
            value: null,
            sid: menus[MENUS.RID][0],
            disabled: false,
            style: BTN_STYLE_INACTIVE,
          });
          count++;
        }
      });

      return res;
    },

    /** SP1用のボタンデータを作成 */
    createBtnDataSP1(controlStateList) {
      return [
        {
          name: this.$t("individual.label_button_open"),
          key: 1,
          style: BTN_STYLE_INACTIVE,
          disabled: false,
          value: controlStateList[1].value,
        },
        {
          name: this.$t("individual.label_button_close"),
          key: 2,
          style: BTN_STYLE_INACTIVE,
          disabled: false,
          value: controlStateList[2].value,
        },
      ];
    },

    /** SP15用のボタンデータを作成 */
    createBtnDataSP15(controlStateList) {
      const res = [];

      // [実行]ボタンのみを作成
      const uuid = this.selectedAtFunc[MENUS.ATID].slice(0, -2);
      const itemNameKey = `at${this.systemId}.${uuid}.${I18N.ITEM_NAME}`;
      const itemName = this.$te(itemNameKey) ? this.$t(itemNameKey) : "";
      res.push({
        name: itemName,
        key: 0,
        value: controlStateList[0].value,
        sid: this.selectedAtFunc[MENUS.SID2E2F][0],
        disabled: false,
        style: BTN_STYLE_INACTIVE,
      });

      return res;
    },

    /**
     * 変換情報を取得する。
     * @returns 変換情報取得の成否
     */
    async getATConversion() {
      const convRes = await atUtil.getATConversion(this, [this.activeTestId]);
      if (!convRes) return false;
      this.conversionInfoList = convRes.data.conversion_list;
      return true;
    },

    /**
     * selectedAtFuncから選択中のボタンのmenusデータを取得する。
     * @returns 選択中のボタンのmenusデータ
     */
    getMenusData() {
      switch (this.requirementId) {
        case REQ_ID.SP24:
          return this.selectedAtFunc[MENUS.VALS][0][this.selectedBtn.key];
        default:
          return this.selectedAtFunc;
      }
    },

    /** SP1ModalからdoStartの呼び出し */
    async sp1DoStart() {
      await this.doStart(this.controlStateList[0].value);
      this.selectedBtn = null;
      if (this.$refs.sp1_modal && !this.$refs.sp1_modal.isConfirmProcess) {
        // 実行確認表示(キャリブレーション有)実行中はスピナーOFFしない
        this.$refs.sp1_modal.isLoading = false;
      }
    },

    /******************** 以下API ********************/

    /**
     * 駆動API通信開始
     * @param {*} specifiedControlState control state値を指定して実行
     * @returns {*} response
     */
    async doStart(specifiedControlState = null) {
      this.isStopping = false;

      if (specifiedControlState) {
        // control state指定の場合(SP1Modalから実行)
        this.selectedBtn = {};
        this.selectedBtn.value = specifiedControlState;
      }
      // API実行パラメータ作成
      const idx = (
        this.requirementId === REQ_ID.SP10 ||
        this.requirementId === REQ_ID.SP1
      ) ? 0 : this.selectedBtn.key;
      const token = session.getToken();

      const requestBody = atUtil.genReqBodyStart(
        token.id_token,
        this.selectedAtFunc,
        this.selectedBtn.value,
        idx
      );

      // start API実行
      const response = await atUtil.pollingAtApi(
        this,
        this.netAppId,
        this.systemId,
        this.activeTestId,
        requestBody,
        // エラー発生のコールバック
        (errorType) => {
          errorType;
          atUtil.showUnexpectedError(this);
        },
        // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
        (result) => {
          atUtil.validateErrorResult(this, result);
        },
        // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
        (result) => {
          atUtil.validateErrorResult(this, result);
        },
        // GET API レスポンスチェックがin_progress時のコールバック
        () => {
          this.flow = FLOW.EXECUTE;
          return config.INDIVIDUAL_POLLING_RESULT_CONTINUE;
        },
        // GET API レスポンスチェックがcompleted時のコールバック
        (getApiResponse, self) => {
          if (atUtil.isErrorAbort(getApiResponse.data[AT_EXE.REQUEST_RESULT])) {
            // 開始のポーリングでCCエラー中断のレスポンスは、データモニタでエラーとなった場合のため、通信エラーへ遷移
            // ただしデータモニタ起点で既にエラー処理をおこなっている場合は、そのままポーリング終了
            if (!FLOW_ERROR.includes(self.flow)) {
              self.flow = FLOW.ERR;
              // エラー時に停止処理なしでエラー終了するものは、ERR_KEY.COMMONでterminateのみ実施
              const errorKey = NO_ERROR_STOP_REQ.includes(this.requirementId) ? ERR_KEY.COMMON : ERR_KEY.COMMUNICATION;
              self.doErrorProcess(errorKey);
            }
            return config.INDIVIDUAL_POLLING_RESULT_FINISH;
          }

          const monitoringStatusData = atUtil.getMonitoringStatusData(getApiResponse.data[AT_EXE.USER_DATA]);
          let result = config.INDIVIDUAL_POLLING_RESULT_ERROR;
          const isExeOnly =
            getApiResponse.data[AT_EXE.USER_DATA].length === 0 &&
            getApiResponse.data[AT_EXE.MNTR_STATUS] === MNTR_STATUS.COMPLETE;
          if (
            isExeOnly ||
            !(AT_EXE.MNTR_STATUS in getApiResponse.data) ||
            !getApiResponse.data[AT_EXE.MNTR_STATUS]
          ) {
            // 実行
            if (isExeOnly || monitoringStatusData[AT_EXE.RES_TYPE] === RES_TYPE.OK) {
              // 正常終了
              if (!self.isStopping) {
                // 実行監視に遷移
                self.flow = FLOW.CHECK_EXE;
              }
              result = config.INDIVIDUAL_POLLING_RESULT_CONTINUE;
            } else {
              // 異常終了
              switch (self.requirementId) {
                case REQ_ID.SP10:
                case REQ_ID.SP15: {
                  const nrc = atUtil.getNRCStatus(monitoringStatusData[AT_EXE.VAL]);
                  if (nrc === NRC_CODE.X22) {
                    // NRCエラーへ遷移
                    self.flow = FLOW.ERR_NRC;
                    self.doErrorProcess(ERR_KEY.NRC);
                  } else {
                    // 共通エラーへ遷移
                    self.flow = FLOW.ERR;
                    self.doErrorProcess(ERR_KEY.COMMON);
                  }
                  result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
                  break;
                }
                case REQ_ID.SP22: {
                  const nrc = atUtil.getNRCStatus(monitoringStatusData[AT_EXE.VAL]);
                  if (nrc === NRC_CODE.X22) {
                    // NRCエラーへ遷移
                    self.flow = FLOW.ERR_NRC;
                    self.doErrorProcess(ERR_KEY.NRC);
                  } else {
                    // 通信エラーへ遷移
                    self.flow = FLOW.ERR_COMMUNICATE;
                    self.doErrorProcess(ERR_KEY.COMMUNICATION);
                  }
                  result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
                  break;
                }
                case REQ_ID.SP24: {
                  const nrc = atUtil.getNRCStatus(monitoringStatusData[AT_EXE.VAL]);
                  if (
                    [NRC_CODE.X11, NRC_CODE.X12, NRC_CODE.X31].includes(nrc)
                  ) {
                    // NRCエラーへ遷移
                    self.flow = FLOW.ERR_NRC;
                    self.doErrorProcess(ERR_KEY.NRC);
                  } else {
                    // 通信エラーへ遷移
                    self.flow = FLOW.ERR_COMMUNICATE;
                    self.doErrorProcess(ERR_KEY.COMMUNICATION);
                  }
                  result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
                  break;
                }
                case REQ_ID.SP1:
                  // 共通エラーへ遷移
                  self.flow = FLOW.ERR;
                  self.doErrorProcess(ERR_KEY.COMMON);
                  result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
                  break;
                default:
                  // 通信エラーへ遷移
                  self.flow = FLOW.ERR_COMMUNICATE;
                  self.doErrorProcess(ERR_KEY.COMMUNICATION);
                  result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
                  break;
              }
            }
          } else {
            // 実行監視
            if (monitoringStatusData[AT_EXE.RES_TYPE] === RES_TYPE.OK) {
              // 正常終了
              if (!self.isStopping) {
                self.flow = FLOW.CHECK_EXE;
              }
              const menusData = self.getMenusData();
              const status = atUtil.getMonitoringStatus(monitoringStatusData[AT_EXE.VAL], self.requirementId);
              // 要求仕様書から継続条件などが指定される場合
              switch (self.requirementId) {
                case REQ_ID.SP1:
                  if (self.selectedBtn.value === self.controlStateList[0].value) {
                    // ControlState = 0002 で送っていた場合
                    if (status === "0100") {
                      // ポーリング続行
                      result = config.INDIVIDUAL_POLLING_RESULT_CONTINUE;
                    } else if (status === "0101") {
                      // SP1Modal実行確認表示(キャリブレーション有)
                      self.$refs.sp1_modal.confirmExecution();
                      result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
                    } else {
                      // キャリブレーション失敗表示
                      self.$refs.sp1_modal.phase = SP1_PHASE.CALIBRATION_FAILURE;
                      self.doTerminate();
                      self.displayInit();
                      result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
                    }
                  } else if (self.selectedBtn.value === self.controlStateList[1].value) {
                    // ControlState = 0001 で送っていた場合
                    if (status === "0112") {
                      // AT文言表示、ポーリング続行
                      self.message = atUtil.getAtMsg(
                        self,
                        self.systemId,
                        self.requirementId,
                        parseInt(status, 16),
                        self.isDriveFlow
                      );
                      result = config.INDIVIDUAL_POLLING_RESULT_CONTINUE;
                    } else if (status === "0113") {
                      // 実行へ遷移
                      self.message = atUtil.getAtMsg(
                        self,
                        self.systemId,
                        self.requirementId,
                        parseInt(status, 16),
                        self.isDriveFlow
                      );
                      atUtil.doTerminate(self, false);
                      self.displayInit();
                      result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
                    } else {
                      // STATUSエラーを表示
                      self.showStatusError(atUtil.getStatusMsg(self, self.systemId, parseInt(status, 16)));
                      result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
                    }
                  } else if (self.selectedBtn.value === self.controlStateList[2].value) {
                    // ControlState = 0000 で送っていた場合
                    if (status === "0110") {
                      // AT文言表示、ポーリング続行
                      self.message = atUtil.getAtMsg(
                        self,
                        self.systemId,
                        self.requirementId,
                        parseInt(status, 16),
                        self.isDriveFlow
                      );
                      result = config.INDIVIDUAL_POLLING_RESULT_CONTINUE;
                    } else if (status === "0111") {
                      // 実行へ遷移
                      self.message = atUtil.getAtMsg(
                        self,
                        self.systemId,
                        self.requirementId,
                        parseInt(status, 16),
                        self.isDriveFlow
                      );
                      atUtil.doTerminate(self, false);
                      self.displayInit();
                      result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
                    } else {
                      // STATUSエラーを表示
                      self.showStatusError(atUtil.getStatusMsg(self, self.systemId, parseInt(status, 16)));
                      result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
                    }
                  }
                  return result;
                default:
                  break;
              }
              if (
                menusData[MENUS.EXE_MNTR_CONTINUE] &&
                menusData[MENUS.EXE_MNTR_CONTINUE].includes(status)
              ) {
                // 実行監視継続条件
                // 現状、ボタンでは実行監視継続条件時に必ずAT文言表示・ポーリングの継続をする
                //上記以外が行われる要求IDがある場合は分岐させること
                // AT文言表示
                switch (self.requirementId) {
                  case REQ_ID.SP1:
                    // 何もしない
                    break;
                  default:
                    self.message = atUtil.getAtMsg(
                      self,
                      self.systemId,
                      self.requirementId,
                      parseInt(status, 16),
                      self.isDriveFlow
                    );
                    result = config.INDIVIDUAL_POLLING_RESULT_CONTINUE;
                }
              } else if (
                menusData[MENUS.EXE_MNTR_TERMINATE] &&
                menusData[MENUS.EXE_MNTR_TERMINATE].includes(status)
              ) {
                // 実行監視終了条件
                switch (self.requirementId) {
                  case REQ_ID.SP10:
                    // 停止処理をしていないなら駆動停止ボタン押下時の処理を実行
                    !self.isStopping && self.stopDrive();
                    result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
                    break;
                  case REQ_ID.SP24: {
                    // STATUSエラーへ遷移
                    const atMsg = atUtil.getStatusMsg(
                      self,
                      self.systemId,
                      parseInt(status, 16),
                    );
                    self.flow = FLOW.ERR_STATUS;
                    self.doErrorProcess(ERR_KEY.STATUS, atMsg);
                    result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
                    break;
                  }
                  default:
                    // AT文言表示
                    self.message = atUtil.getAtMsg(
                      self,
                      self.systemId,
                      self.requirementId,
                      parseInt(status, 16),
                      self.isDriveFlow
                    );
                    // 停止処理をしていないなら初期表示処理
                    if (!self.isStopping) {
                      atUtil.doTerminate(self, false);
                      self.displayInit();
                    }
                    result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
                    break;
                }
              } else {
                // それ以外
                switch (self.requirementId) {
                  case REQ_ID.SP10:
                  case REQ_ID.SP15:
                  case REQ_ID.SP24: {
                    // STATUSエラーへ遷移
                    const atMsg = atUtil.getStatusMsg(
                      self,
                      self.systemId,
                      parseInt(status, 16),
                    );
                    self.flow = FLOW.ERR_STATUS;
                    self.doErrorProcess(ERR_KEY.STATUS, atMsg);
                    result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
                    break;
                  }
                  default:
                    // 通信エラーへ遷移
                    self.flow = FLOW.ERR_COMMUNICATE;
                    self.doErrorProcess(ERR_KEY.COMMUNICATION);
                    result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
                    break;
                }
              }
            } else {
              // 異常終了
              switch (self.requirementId) {
                case REQ_ID.SP1:
                case REQ_ID.SP10:
                case REQ_ID.SP15:
                  // 共通エラーへ遷移
                  self.flow = FLOW.ERR;
                  self.doErrorProcess(ERR_KEY.COMMON);
                  result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
                  break;
                default:
                  // 通信エラーへ遷移
                  self.flow = FLOW.ERR_COMMUNICATE;
                  self.doErrorProcess(ERR_KEY.COMMUNICATION);
                  result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
                  break;
              }
            }
          }
          if (self.isStopping) {
            // 停止処理が開始されたのでstartのポーリングを終了させる
            result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
          }
          return result;
        },
        // GET API レスポンスチェックがfailed時のコールバック
        (getApiResponse, self) => {
          // 共通エラーへ遷移
          self.flow = FLOW.ERR;
          self.doErrorProcess(ERR_KEY.COMMON);
        },
      );

      return response;
    },

    /**
     * 駆動API通信停止
     */
    async doStop() {
      this.isStopping = true;

      // API実行パラメータ作成
      const idx = this.requirementId === REQ_ID.SP10
        ? 0
        : this.selectedBtn.key;
      const token = session.getToken();
      const requestBody = atUtil.genReqBodyStop(
        token.id_token,
        this.selectedAtFunc,
        idx
      );

      // stop API実行
      const response = await atUtil.pollingAtApi(
        this,
        this.netAppId,
        this.systemId,
        this.activeTestId,
        requestBody,
        // エラー発生のコールバック
        (errorType) => {
          errorType;
          atUtil.showUnexpectedError(this);
        },
        // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
        (result) => {
          atUtil.validateErrorResult(this, result);
        },
        // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
        (result) => {
          atUtil.validateErrorResult(this, result);
        },
        // GET API レスポンスチェックがin_progress時のコールバック
        () => {
          return config.INDIVIDUAL_POLLING_RESULT_CONTINUE;
        },
        // GET API レスポンスチェックがcompleted時のコールバック
        (getApiResponse, self) => {
          let result = "";
          if (getApiResponse.data[AT_EXE.REQUEST_RESULT] === REQUEST_RESULT.IGNORE) {
            // 停止要求が無効だった場合、何もせずに処理を終了する
            result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
            return result;
          }

          const monitoringStatusData = atUtil.getMonitoringStatusData(getApiResponse.data[AT_EXE.USER_DATA]);
          result = config.INDIVIDUAL_POLLING_RESULT_ERROR;
          const isStopOnly =
            getApiResponse.data[AT_EXE.USER_DATA].length === 0 &&
            getApiResponse.data[AT_EXE.MNTR_STATUS] === MNTR_STATUS.COMPLETE;
          if (
            isStopOnly ||
            !(AT_EXE.MNTR_STATUS in getApiResponse.data) ||
            !getApiResponse.data[AT_EXE.MNTR_STATUS]
          ) {
            // 停止
            if (
              isStopOnly || monitoringStatusData[AT_EXE.RES_TYPE] === RES_TYPE.OK
            ) {
              // 正常終了
              switch (self.requirementId) {
                case REQ_ID.SP10:
                case REQ_ID.SP15:
                case REQ_ID.SP27:
                  result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
                  break;
                default:
                  // 停止監視に遷移
                  self.flow = FLOW.CHECK_STOP;
                  result = config.INDIVIDUAL_POLLING_RESULT_CONTINUE;
                  break;
              }
            } else {
              // 異常終了
              switch (self.requirementId) {
                case REQ_ID.SP27: {
                  const nrc = atUtil.getNRCStatus(monitoringStatusData[AT_EXE.VAL]);
                  // NRC0x24の場合は後処理に遷移する
                  if (nrc === NRC_CODE.X24) {
                    result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
                  } else {
                    // 共通エラーへ遷移
                    self.flow = FLOW.ERR;
                    self.doErrorProcess(ERR_KEY.COMMON);
                    result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
                  }
                  break;
                }
                default:
                  // 共通エラーへ遷移
                  self.flow = FLOW.ERR;
                  self.doErrorProcess(ERR_KEY.COMMON);
                  result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
                  break;
              }
            }
          } else {
            // 停止監視
            if (monitoringStatusData[AT_EXE.RES_TYPE] === RES_TYPE.OK) {
              // 正常終了
              const menusData = self.getMenusData();
              const status = atUtil.getMonitoringStatus(monitoringStatusData[AT_EXE.VAL], self.requirementId);
              const stopMntrContinue =
                menusData[MENUS.STOP_MNTR_CONTINUE] &&
                menusData[MENUS.STOP_MNTR_CONTINUE].includes(status);
              const stopMntrTerminate =
                menusData[MENUS.STOP_MNTR_TERMINATE] &&
                menusData[MENUS.STOP_MNTR_TERMINATE].includes(status);

              if (stopMntrContinue) {
                // 停止監視継続条件
                switch (self.requirementId) {
                  case REQ_ID.SP20:
                  case REQ_ID.SP22:
                  case REQ_ID.SP24:
                    // AT文言表示
                    self.message = atUtil.getAtMsg(
                      self,
                      self.systemId,
                      self.requirementId,
                      parseInt(status, 16),
                      self.isDriveFlow
                    );
                    result = config.INDIVIDUAL_POLLING_RESULT_CONTINUE;
                    break;
                  default:
                    // 共通エラーへ遷移
                    self.flow = FLOW.ERR;
                    self.doErrorProcess(ERR_KEY.COMMON);
                    result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
                    break;
                }
              } else if (stopMntrTerminate) {
                // 停止監視終了条件
                switch (self.requirementId) {
                  case REQ_ID.SP20:
                  case REQ_ID.SP22:
                  case REQ_ID.SP24:
                    // AT文言表示
                    self.message = atUtil.getAtMsg(
                      self,
                      self.systemId,
                      self.requirementId,
                      parseInt(status, 16),
                      self.isDriveFlow,
                      null,
                      true
                    );
                    // 初期表示処理
                    self.displayInit();
                    result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
                    break;
                  default:
                    // 共通エラーへ遷移
                    self.flow = FLOW.ERR;
                    self.doErrorProcess(ERR_KEY.COMMON);
                    result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
                    break;
                }
              } else {
                // それ以外
                switch (self.requirementId) {
                  case REQ_ID.SP22:
                  case REQ_ID.SP24:
                    // AT文言表示
                    self.message = atUtil.getAtMsg(
                      self,
                      self.systemId,
                      self.requirementId,
                      parseInt(status, 16),
                      self.isDriveFlow,
                      null,
                      true
                    );
                    // 初期表示処理
                    self.displayInit();
                    result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
                    break;
                  default:
                    // 共通エラーへ遷移
                    self.flow = FLOW.ERR;
                    self.doErrorProcess(ERR_KEY.COMMON);
                    result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
                    break;
                }
              }
            } else {
              // 異常終了
              // 現状、ボタンでは異常終了時に必ず共通エラーへ遷移する
              // 共通エラーへ遷移しない要求IDがある場合は分岐させること
              // 共通エラーへ遷移
              self.flow = FLOW.ERR;
              self.doErrorProcess(ERR_KEY.COMMON);
              result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
            }
          }
          return result;
        },
        // GET API レスポンスチェックがfailed時のコールバック
        (getApiResponse, self) => {
          // 共通エラーへ遷移
          self.flow = FLOW.ERR;
          self.doErrorProcess(ERR_KEY.COMMON);
        }
      );

      return response;
    },

    /** 正常時のAT終了処理 
     * @async
     * @return {boolean} terminate result - false is faild.
     */
    async doTerminate() {
      const response = await atUtil.doTerminate(this, false);
      if (response) {
        if (response.data.status === config.STATUS_DMAT_COMPLETED) {
          // 正常終了
          if (!response.data.user_data
            || response.data.user_data.length === 0
            || response.data.user_data[0][AT_EXE.RES_TYPE] === RES_TYPE.OK) {
            return true;
          }
        }
      }

      return false;
    },

    /******************** エラー処理 ********************/
    /** 通信エラー画面処理 */
    async communicateError() {
      this.isStopping = true;

      // API実行パラメータ作成
      const idx = this.requirementId === REQ_ID.SP10
        ? 0
        : this.selectedBtn.key;
      const token = session.getToken();
      const requestBody = atUtil.genReqBodyStop(
        token.id_token,
        this.selectedAtFunc,
        idx,
        ERR_TYPE.COMMUNICATE
      );

      const response = await atUtil.pollingAtApi(
        this,
        this.netAppId,
        this.systemId,
        this.activeTestId,
        requestBody,
        // エラー発生のコールバック
        (errorType) => {
          errorType;
          // 他のエラーが発生していない場合に限り、例外エラーダイアログを表示する
          if (!FLOW_ERROR.includes(this.flow)) {
            atUtil.showUnexpectedError(this);
          }
        },
        // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
        (result) => {
          atUtil.validateErrorResult(this, result);
        },
        // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
        (result) => {
          atUtil.validateErrorResult(this, result);
        },
        // GET API レスポンスチェックがin_progress時のコールバック
        () => { },
        // GET API レスポンスチェックがcompleted時のコールバック
        (getApiResponse, self) => {
          const monitoringStatusData = atUtil.getMonitoringStatusData(getApiResponse.data[AT_EXE.USER_DATA]);
          let result = config.INDIVIDUAL_POLLING_RESULT_ERROR;
          const isStopOnly =
            getApiResponse.data[AT_EXE.USER_DATA].length === 0 &&
            getApiResponse.data[AT_EXE.MNTR_STATUS] === MNTR_STATUS.COMPLETE;
          if (
            isStopOnly ||
            !(AT_EXE.MNTR_STATUS in getApiResponse.data) ||
            !getApiResponse.data[AT_EXE.MNTR_STATUS]
          ) {
            // 停止
            if (isStopOnly || monitoringStatusData[AT_EXE.RES_TYPE] === RES_TYPE.OK) {
              // 正常終了
              switch (self.requirementId) {
                case REQ_ID.SP27:
                  // 通信エラーを表示
                  atUtil.showUnexpectedError(
                    self,
                    ERR_KEY.COMMUNICATION,
                    self.requirementId,
                    null,
                    async () => {
                      // 停止時に他の操作をさせないようにする
                      self.displayProcessing = true;
                      await atUtil.doTerminate(self, true);
                      self.displayInit();
                      self.$emit('at-failure');
                      self.displayProcessing = false;
                    }
                  );
                  result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
                  break;
                default:
                  // 停止監視に遷移
                  result = config.INDIVIDUAL_POLLING_RESULT_CONTINUE;
                  break;
              }
            } else {
              // 異常終了
              // 共通エラーへ遷移
              self.flow = FLOW.ERR;
              self.doErrorProcess(ERR_KEY.COMMON);
              result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
            }
          } else {
            // 停止監視
            if (monitoringStatusData[AT_EXE.RES_TYPE] === RES_TYPE.OK) {
              // 正常終了
              const menusData = self.getMenusData();
              const status = atUtil.getMonitoringStatus(monitoringStatusData[AT_EXE.VAL], self.requirementId);
              const stopMntrContinue =
                menusData[MENUS.STOP_MNTR_CONTINUE] &&
                menusData[MENUS.STOP_MNTR_CONTINUE].includes(status);

              if (stopMntrContinue) {
                // 停止監視継続条件
                switch (self.requirementId) {
                  case REQ_ID.SP20:
                  case REQ_ID.SP22:
                  case REQ_ID.SP24:
                    result = config.INDIVIDUAL_POLLING_RESULT_CONTINUE;
                    break;
                  default:
                    break;
                }
              } else {
                // それ以外
                // 通信エラーを表示
                atUtil.showUnexpectedError(
                  self,
                  ERR_KEY.COMMUNICATION,
                  self.requirementId,
                  null,
                  async () => {
                    // 停止時に他の操作をさせないようにする
                    self.displayProcessing = true;
                    await atUtil.doTerminate(self, true);
                    self.displayInit();
                    self.$emit('at-failure');
                    self.displayProcessing = false;
                  }
                );
                result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
              }
            } else {
              // 異常終了
              // 共通エラーへ遷移
              self.flow = FLOW.ERR;
              self.doErrorProcess(ERR_KEY.COMMON);
              result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
            }
          }
          return result;
        },
        // GET API レスポンスチェックがfailed時のコールバック
        (_, self) => {
          // 共通エラーへ遷移
          self.flow = FLOW.ERR;
          self.doErrorProcess(ERR_KEY.COMMON);
        }
      );
      return response;
    },

    /**
     * STATUSエラー画面処理
     * @param {string} [atMsg=""] 表示するAT文言
     */
    async statusError(atMsg = "") {
      this.isStopping = true;

      // API実行パラメータ作成
      const idx = this.requirementId === REQ_ID.SP10
        ? 0
        : this.selectedBtn.key;
      const token = session.getToken();
      const requestBody = atUtil.genReqBodyStop(
        token.id_token,
        this.selectedAtFunc,
        idx,
        ERR_TYPE.STATUS
      );

      const response = await atUtil.pollingAtApi(
        this,
        this.netAppId,
        this.systemId,
        this.activeTestId,
        requestBody,
        // エラー発生のコールバック
        () => {
          // 異常終了: STATUSエラー画面表示
          this.showStatusError(atMsg);
        },
        // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
        () => {
          // 異常終了: STATUSエラー画面表示
          this.showStatusError(atMsg);
        },
        // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
        () => {
          // 異常終了: STATUSエラー画面表示
          this.showStatusError(atMsg);
        },
        // GET API レスポンスチェックがin_progress時のコールバック
        () => { },
        // GET API レスポンスチェックがcompleted時のコールバック
        (getApiResponse, self) => {
          const monitoringStatusData = atUtil.getMonitoringStatusData(getApiResponse.data[AT_EXE.USER_DATA]);
          let result = config.INDIVIDUAL_POLLING_RESULT_ERROR;
          const isStopOnly =
            getApiResponse.data[AT_EXE.USER_DATA].length === 0 &&
            getApiResponse.data[AT_EXE.MNTR_STATUS] === MNTR_STATUS.COMPLETE;
          if (
            isStopOnly ||
            !(AT_EXE.MNTR_STATUS in getApiResponse.data) ||
            !getApiResponse.data[AT_EXE.MNTR_STATUS]
          ) {
            // 停止
            if (isStopOnly || monitoringStatusData[AT_EXE.RES_TYPE] === RES_TYPE.OK) {
              // 正常終了
              switch (self.requirementId) {
                case REQ_ID.SP10:
                case REQ_ID.SP15:
                  // AT文言表示
                  // STATUSエラーを表示
                  atUtil.showUnexpectedError(
                    self,
                    ERR_KEY.STATUS,
                    self.requirementId,
                    atMsg,
                    async () => {
                      await atUtil.doTerminate(self, true);
                      self.displayInit();
                      self.$emit('at-failure');
                    }
                  );
                  result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
                  break;
                default:
                  // 停止監視に遷移
                  result = config.INDIVIDUAL_POLLING_RESULT_CONTINUE;
                  break;
              }
            } else {
              // 異常終了: STATUSエラー画面表示
              self.showStatusError(atMsg);
              result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
            }
          } else {
            // 停止監視
            if (monitoringStatusData[AT_EXE.RES_TYPE] === RES_TYPE.OK) {
              // 正常終了
              const menusData = self.getMenusData();
              const status = atUtil.getMonitoringStatus(monitoringStatusData[AT_EXE.VAL], self.requirementId);
              const stopMntrContinue =
                menusData[MENUS.STOP_MNTR_CONTINUE] &&
                menusData[MENUS.STOP_MNTR_CONTINUE].includes(status);

              if (stopMntrContinue) {
                // 停止監視継続条件
                switch (self.requirementId) {
                  case REQ_ID.SP24:
                    result = config.INDIVIDUAL_POLLING_RESULT_CONTINUE;
                    break;
                  default:
                    break;
                }
              } else {
                // それ以外
                // STATUSエラーを表示
                atUtil.showUnexpectedError(
                  self,
                  ERR_KEY.STATUS,
                  self.requirementId,
                  atMsg,
                  async () => {
                    // 停止時に他の操作をさせないようにする
                    self.displayProcessing = true;
                    await atUtil.doTerminate(self, true);
                    self.displayInit();
                    self.$emit('at-failure');
                    self.displayProcessing = false;
                  }
                );
                result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
              }
            } else {
              // 異常終了: STATUSエラー画面表示
              self.showStatusError(atMsg);
              result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
            }
          }
          return result;
        },
        // GET API レスポンスチェックがfailed時のコールバック
        () => {
          // 異常終了: STATUSエラー画面表示
          this.showStatusError(atMsg);
        }
      );
      return response;
    },

    /** NRCエラー画面処理 */
    async nrcError() {
      this.isStopping = true;

      // APIを呼び出さないもの
      switch (this.requirementId) {
        case REQ_ID.SP22:
          // NRCエラーを表示
          atUtil.showUnexpectedError(
            this,
            ERR_KEY.NRC,
            this.requirementId,
            null,
            async () => {
              // 停止時に他の操作をさせないようにする
              this.displayProcessing = true;
              await atUtil.doTerminate(this, true);
              this.displayInit();
              this.$emit('at-failure');
              this.displayProcessing = false;
            }
          );
          return null;
        default:
          break;
      }

      // API実行パラメータ作成
      const idx = this.requirementId === REQ_ID.SP10
        ? 0
        : this.selectedBtn.key;
      const token = session.getToken();
      const requestBody = atUtil.genReqBodyStop(
        token.id_token,
        this.selectedAtFunc,
        idx,
        ERR_TYPE.NRC
      );

      const response = await atUtil.pollingAtApi(
        this,
        this.netAppId,
        this.systemId,
        this.activeTestId,
        requestBody,
        // エラー発生のコールバック
        (errorType) => {
          errorType;
          atUtil.showUnexpectedError(this);
        },
        // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
        (result) => {
          atUtil.validateErrorResult(this, result);
        },
        // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
        (result) => {
          atUtil.validateErrorResult(this, result);
        },
        // GET API レスポンスチェックがin_progress時のコールバック
        () => { },
        // GET API レスポンスチェックがcompleted時のコールバック
        (getApiResponse, self) => {
          const monitoringStatusData = atUtil.getMonitoringStatusData(getApiResponse.data[AT_EXE.USER_DATA]);
          let result = config.INDIVIDUAL_POLLING_RESULT_ERROR;
          const isStopOnly =
            getApiResponse.data[AT_EXE.USER_DATA].length === 0 &&
            getApiResponse.data[AT_EXE.MNTR_STATUS] === MNTR_STATUS.COMPLETE;
          if (
            isStopOnly ||
            !(AT_EXE.MNTR_STATUS in getApiResponse.data) ||
            !getApiResponse.data[AT_EXE.MNTR_STATUS]
          ) {
            // 停止
            if (isStopOnly || monitoringStatusData[AT_EXE.RES_TYPE] === RES_TYPE.OK) {
              // 正常終了
              switch (self.requirementId) {
                case REQ_ID.SP10:
                case REQ_ID.SP15:
                  // NRCエラーを表示
                  atUtil.showUnexpectedError(
                    self,
                    ERR_KEY.NRC,
                    self.requirementId,
                    null,
                    async () => {
                      // 停止時に他の操作をさせないようにする
                      self.displayProcessing = true;
                      await atUtil.doTerminate(self, true);
                      self.displayInit();
                      self.$emit('at-failure');
                      self.displayProcessing = false;
                    }
                  );
                  result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
                  break;
                default:
                  // 停止監視に遷移
                  result = config.INDIVIDUAL_POLLING_RESULT_CONTINUE;
                  break;
              }
            } else {
              // 異常終了: NRCエラー画面表示
              self.showNrcError();
              result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
            }
          } else {
            // 停止監視
            if (monitoringStatusData[AT_EXE.RES_TYPE] === RES_TYPE.OK) {
              // 正常終了
              const menusData = self.getMenusData();
              const status = atUtil.getMonitoringStatus(monitoringStatusData[AT_EXE.VAL], self.requirementId);
              const stopMntrContinue =
                menusData[MENUS.STOP_MNTR_CONTINUE] &&
                menusData[MENUS.STOP_MNTR_CONTINUE].includes(status);

              if (stopMntrContinue) {
                // 停止監視継続条件
                switch (self.requirementId) {
                  case REQ_ID.SP24:
                    result = config.INDIVIDUAL_POLLING_RESULT_CONTINUE;
                    break;
                  default:
                    break;
                }
              } else {
                // それ以外
                // NRCエラーを表示
                atUtil.showUnexpectedError(
                  self,
                  ERR_KEY.NRC,
                  self.requirementId,
                  null,
                  async () => {
                    // 停止時に他の操作をさせないようにする
                    self.displayProcessing = true;
                    await atUtil.doTerminate(self, true);
                    self.displayInit();
                    self.$emit('at-failure');
                    self.displayProcessing = false;
                  }
                );
                result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
              }
            } else {
              // 異常終了: NRCエラー画面表示
              self.showNrcError();
              result = config.INDIVIDUAL_POLLING_RESULT_FINISH;
            }
          }
          return result;
        },
        // GET API レスポンスチェックがfailed時のコールバック
        () => {
          // 共通エラーへ遷移
          self.flow = FLOW.ERR;
          self.doErrorProcess(ERR_KEY.COMMON);
        }
      );
      return response;
    },

    /** 共通エラー画面処理 */
    commonError() {
      // 共通エラーを表示
      atUtil.showUnexpectedError(
        this,
        ERR_KEY.COMMON,
        this.requirementId,
        null, // 共通エラーはnull
        async () => {
          // 停止時に他の操作をさせないようにする
          this.displayProcessing = true;
          await atUtil.doTerminate(this, true);
          this.displayInit();
          this.$emit('at-failure');
          this.displayProcessing = false;
        }
      );
    },

    /**
     * STATUSエラー表示（表示のみ）
     * @param {string} atMsg STATUSエラーで表示するAT文言
     */
    showStatusError(atMsg) {
      atUtil.showUnexpectedError(
        this,
        ERR_KEY.STATUS,
        this.requirementId,
        atMsg,
        async () => {
          // Terminate中に他の操作をさせないようにする
          this.displayProcessing = true;
          await atUtil.doTerminate(this, true);
          this.displayInit();
          this.$emit('at-failure');
          this.displayProcessing = false;
        }
      );
    },

    /** NRCエラー画面表示 */
    showNrcError() {
      atUtil.showUnexpectedError(
        this,
        ERR_KEY.NRC,
        this.requirementId,
        null,
        async () => {
          // Terminate中に他の操作をさせないようにする
          this.displayProcessing = true;
          await atUtil.doTerminate(this, true);
          this.displayInit();
          this.$emit('at-failure');
          this.displayProcessing = false;
        }
      );
    },

    /** 
     * エラー処理の振り分け
     * @param {*} errKey エラーキー
     * @param {string} [atMsg=""] STATUSエラーで表示するAT文言
     */
    async doErrorProcess(errKey, atMsg = "") {
      switch (errKey) {
        case ERR_KEY.COMMUNICATION:
          await this.communicateError();
          break;
        case ERR_KEY.STATUS:
          await this.statusError(atMsg);
          break;
        case ERR_KEY.NRC:
          await this.nrcError();
          break;
        case ERR_KEY.COMMON:
        default:
          this.commonError();
          break;
      }
    },

    /** キャンセル処理 */
    async cancelExec() {
      // 駆動中であれば停止する。ただし他のエラー処理を既に実行していれば何もしない
      if (this.isDriveFlow && !FLOW_ERROR.includes(this.flow) && !NO_ERROR_STOP_REQ.includes(this.requirementId)) {
        this.flow = FLOW.STOP;
        await this.doStop();
      }
    },

    // 親コンポーネントへ失敗通知
    atFailure() {
      this.$emit('at-failure');
    },

    setSignout() {
      this.signout = true;
    },
  },

  mounted() {
    // noop
  },
};
